import { useLoader } from "@/context/LoaderContext";
import { useUser } from "@/context/UserContext";
import {
  getCourses,
  syncPdcCourseProgressToRealityThenRefreshQueries,
} from "@/services/courses";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { usePdcExternalCoursesQuery } from "@/hooks/usePdcExternalCoursesQuery";
import { isPdcExternalCourseListEnabled } from "@/utils/pdc-external-course-list";
import { derivePdcCourseListStats } from "@/services/pdcExternal/courses";

function resolvePdcEmbedSrc(courseUrl: string): string {
  const trimmed = courseUrl?.trim();
  if (!trimmed) return "/course/";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.replace(/^\/+/, "");
  return `/course/${path}`;
}

export const useCourses = (enabled = true) => {
  const location = useLocation();
  const loader = useLoader();
  const { userInfo } = useUser();
  const [courseEmbedOpen, setCourseEmbedOpen] = useState(false);
  const [pdcEmbedSrc, setPdcEmbedSrc] = useState("/course/");

  const usePdcList = isPdcExternalCourseListEnabled();

  const nestQuery = useQuery("get-courses", () => getCourses(), {
    enabled: enabled && !usePdcList,
  });

  const pdcQuery = usePdcExternalCoursesQuery({ enabled: enabled && usePdcList });

  const courses = usePdcList ? pdcQuery.data : nestQuery.data;
  const isLoading = usePdcList ? pdcQuery.isLoading : nestQuery.isLoading;

  const pdcListStats = useMemo(() => {
    if (!usePdcList || !courses) return undefined;
    return derivePdcCourseListStats(courses);
  }, [usePdcList, courses]);

  const onGoToCourse = async (courseUrl: string) => {
    if (!userInfo) return;

    if (!userInfo.pdcMemberSynced) {
      toast(
        "Conta ainda não liberada para o PDC. Conclua o cadastro (foto, CPF e área de atuação) e aguarde a sincronização; se já concluiu, atualize a página (F5).",
        { type: "warning" }
      );
      return;
    }

    setPdcEmbedSrc(resolvePdcEmbedSrc(courseUrl));
    setCourseEmbedOpen(true);
  };

  const onCloseCourseEmbed = () => {
    setCourseEmbedOpen(false);
    setPdcEmbedSrc("/course/");
    if (usePdcList) {
      void syncPdcCourseProgressToRealityThenRefreshQueries();
    }
  };

  useEffect(() => {
    if (isLoading) {
      loader.show("get-user-courses");
    } else {
      loader.hide("get-user-courses");
    }
    return () => {
      loader.hide("get-user-courses");
    };
  }, [isLoading, loader]);

  /** Each visit to /courses + fresh PDC list: sync Nest DB and refresh caches. */
  useEffect(() => {
    if (location.pathname !== "/courses") return;
    if (!usePdcList || !enabled || !pdcQuery.isSuccess) return;
    void syncPdcCourseProgressToRealityThenRefreshQueries();
  }, [location.key, location.pathname, usePdcList, enabled, pdcQuery.isSuccess]);

  return {
    courses,
    onGoToCourse,
    courseEmbedOpen,
    onCloseCourseEmbed,
    pdcEmbedSrc,
    pdcListStats,
  };
};
